import type {
  PortalAutoPanel,
  PortalGoogleAutoPanel,
  PortalGoogleAutomotiveReport,
  PortalMetaAutomotiveReport,
} from '../../../../../types/portalAutomotive';
import type { PortalReportLabels } from '../../../../../types/portalReport';
import {
  AUTO_COST_PER_LABELS,
  AUTO_COUNT_LABELS,
  autoPanelIsEmpty,
  autoPanelSpecFor,
  autoPanelVisible,
  contactsVisible,
} from '../../../../../lib/portalReport/automotiveReport';
import {
  GOOGLE_AUTO_NONE_SENTENCE,
  googleAutoNothingRan,
  googleAutoPanelSpecFor,
  googleAutoPanelVisible,
} from '../../../../../lib/portalReport/automotiveGoogle';
import { Band, Figure, SectionHeading } from './controls';
import { GoogleAutoFamilyChart } from './AutomotiveGooglePanels';
import { AutoFamilyChart } from './AutomotiveMetaPanels';
import { ReportHero, type HeroStat } from './ReportHero';
import { DASH, formatReportDate, moneyFull, numberFull, oneDecimal } from './format';

// CLIENT PORTAL RESTYLE — the AUTOMOTIVE HOME page (2026-11-11, round 4 §8).
//
// ⚠️⚠️ THE GOOGLE STRIP IS LIVE (Phase 2); THE WEBSITE STRIP IS STILL A PLACEHOLDER
// that says "Not available yet" (only when the scope has a GA4 property; with none the
// strip and the hero's Website stat are OMITTED): Website analytics arrive in Phase 3, and a strip
// that rendered zeros in the meantime would tell a dealer their website did
// nothing. It is drawn (not omitted) so the structure the page will have is visible
// and nothing appears to be missing by accident. A scope that does not run Google
// has NO Google strip at all (the channel is suppressed, not empty).
//
// ⚠️ Comparison deltas and the 12-month year-on-year use the report's existing
// machinery; this page adds no second one. Hero stats carry no delta here because
// the automotive headline is not yet a PortalChannelBlock.

export const NOT_AVAILABLE_YET = 'Not available yet';

/** Spend per currency, Meta and Google merged — never blended across currencies. */
function mergedSpend(
  auto: PortalMetaAutomotiveReport,
  google: PortalGoogleAutomotiveReport | null | undefined,
): Array<{ currency: string; spend: number }> {
  const by = new Map<string, number>();
  for (const m of auto.headline.metaSpend) by.set(m.currency, (by.get(m.currency) ?? 0) + m.spend);
  for (const g of google?.headline.spend ?? []) by.set(g.currency, (by.get(g.currency) ?? 0) + g.spend);
  return [...by.entries()].map(([currency, spend]) => ({ currency, spend })).sort((a, b) => b.spend - a.spend);
}

function HomeHero({
  auto,
  google,
  labels,
  range,
  showsWebsite,
}: {
  auto: PortalMetaAutomotiveReport;
  google: PortalGoogleAutomotiveReport | null | undefined;
  labels: PortalReportLabels;
  range: { from: string; to: string };
  showsWebsite: boolean;
}) {
  const h = auto.headline;
  const spend = mergedSpend(auto, google);
  const googleConversions = google?.headline.conversions ?? 0;
  const stats: HeroStat[] = [
    {
      label: 'Total ad spend',
      value: spend.length > 0 ? spend.map((m) => moneyFull(m.spend, m.currency)).join(' · ') : null,
      delta: null,
      metric: 'spend',
      unavailable: null,
    },
    {
      label: 'Content views',
      value: numberFull(h.contentViews),
      delta: null,
      metric: 'leads',
      unavailable: null,
    },
    // ⚠️ A dash plus a stated reason, never a 0 — see the header. ABSENT when the
    // scope has no GA4 property (nothing to wait for, so no placeholder).
    ...(showsWebsite
      ? [{ label: 'Website key events', value: null, delta: null, metric: 'leads', unavailable: NOT_AVAILABLE_YET } as HeroStat]
      : []),
  ];
  // ⚠️ Ad conversions = Meta leads + website leads + Google conversions. Google's are
  // fractional, so the total prints to one decimal whenever Google is in it.
  const total = h.leads + googleConversions;
  return (
    <ReportHero
      // ⚠️ The brand's own word for conversions (the existing `leads` label), with
      // the breakdown stated in the intro so the headline can be read against the
      // Meta and Google pages' figures.
      eyebrow={`${labels.leads} · ${formatReportDate(range.from)} – ${formatReportDate(range.to)}`}
      headline={google ? oneDecimal(total) : numberFull(total)}
      intro={
        google
          ? `${numberFull(h.metaLeads)} Meta leads and ${numberFull(h.websiteLeads)} website leads from Meta, plus ${oneDecimal(googleConversions)} Google conversions.`
          : `${numberFull(h.metaLeads)} Meta leads and ${numberFull(h.websiteLeads)} website leads from Meta.`
      }
      stats={stats}
    />
  );
}

/** One card in the Meta strip. */
function StripCard({ panel }: { panel: PortalAutoPanel }) {
  const spec = autoPanelSpecFor(panel.bucket);
  const money = panel.money.length > 0 ? panel.money : null;
  return (
    <section className="rpt-card" aria-label={spec.title} data-home-card={panel.bucket}>
      <h3 className="rpt-h3 rpt-display">{spec.title}</h3>
      <div className="rpt-stat-grid">
        {spec.primary.map((k) => (
          <p key={k} className="rpt-stat">
            <Figure value={numberFull(panel.counts[k])} className="rpt-card-figure" />
            <span className="rpt-mono rpt-stat-label">{AUTO_COUNT_LABELS[k]}</span>
          </p>
        ))}
        {spec.costPer.map((cp) => (
          <p key={cp.key} className="rpt-stat">
            <Figure
              value={
                money
                  ? money
                      .map((m) => {
                        const v = m.costPer[cp.key];
                        return v === null || v === undefined ? DASH : moneyFull(v, m.currency);
                      })
                      .join(' · ')
                  : null
              }
              className="rpt-card-figure"
            />
            <span className="rpt-mono rpt-stat-label">{AUTO_COST_PER_LABELS[cp.key]}</span>
          </p>
        ))}
      </div>
      {autoPanelIsEmpty(panel) && spec.emptySentence && (
        <p className="rpt-note rpt-muted">{spec.emptySentence}</p>
      )}
    </section>
  );
}

/** Contact has no panel; on the Home strip it is a card of its own, ONLY when it exists. */
function ContactCard({ auto }: { auto: PortalMetaAutomotiveReport }) {
  return (
    <section className="rpt-card" aria-label="Contacts" data-home-card="contacts">
      <h3 className="rpt-h3 rpt-display">Contacts</h3>
      <div className="rpt-stat-grid">
        <p className="rpt-stat">
          <Figure value={numberFull(auto.headline.contacts)} className="rpt-card-figure" />
          <span className="rpt-mono rpt-stat-label">{AUTO_COUNT_LABELS.contacts}</span>
        </p>
      </div>
    </section>
  );
}

/** One card in the Google strip: conversions, cost per conversion and spend. */
function GoogleStripCard({ panel }: { panel: PortalGoogleAutoPanel }) {
  const spec = googleAutoPanelSpecFor(panel.group);
  const money = panel.money.length > 0 ? panel.money : null;
  return (
    <section className="rpt-card" aria-label={spec.title} data-home-card={`google-${panel.group}`}>
      <h3 className="rpt-h3 rpt-display">{spec.title}</h3>
      <div className="rpt-stat-grid">
        <p className="rpt-stat">
          <Figure value={oneDecimal(panel.counts.conversions)} className="rpt-card-figure" />
          <span className="rpt-mono rpt-stat-label">Conversions</span>
        </p>
        <p className="rpt-stat">
          <Figure
            value={
              money
                ? money
                    .map((m) => (m.costPerConversion === null ? DASH : moneyFull(m.costPerConversion, m.currency)))
                    .join(' · ')
                : null
            }
            className="rpt-card-figure"
          />
          <span className="rpt-mono rpt-stat-label">Cost per conversion</span>
        </p>
        <p className="rpt-stat">
          <Figure
            value={money ? money.map((m) => moneyFull(m.spend, m.currency)).join(' · ') : null}
            className="rpt-card-figure"
          />
          <span className="rpt-mono rpt-stat-label">Spend</span>
        </p>
      </div>
    </section>
  );
}

function PlaceholderStrip({ title, intro }: { title: string; intro: string }) {
  return (
    <Band>
      <SectionHeading title={title} intro={intro} />
      <section className="rpt-card" aria-label={title} data-home-placeholder>
        <p className="rpt-note rpt-muted">{NOT_AVAILABLE_YET}</p>
      </section>
    </Band>
  );
}

export function AutomotiveHome({
  auto,
  google,
  labels,
  range,
  today,
  showsWebsite,
}: {
  auto: PortalMetaAutomotiveReport;
  /** The Google groups, or null/absent when the scope does not run Google. */
  google?: PortalGoogleAutomotiveReport | null;
  labels: PortalReportLabels;
  range: { from: string; to: string };
  today: string;
  /** Server-resolved: the scope has a GA4 property. Without one the Website strip is omitted. */
  showsWebsite: boolean;
}) {
  const byBucket = (b: PortalAutoPanel['bucket']) => auto.panels.find((p) => p.bucket === b);
  // ⚠️ The Home Meta strip is AIA, Meta lead, Website lead and Contact (round 4 §8).
  // Website lead follows the same hide-when-empty rule as on the Meta page;
  // Contact follows hide-when-zero. AIA and Meta lead always render.
  const strip = (['aia', 'meta_lead', 'website_lead'] as const)
    .map(byBucket)
    .filter((p): p is PortalAutoPanel => p !== undefined && autoPanelVisible(p));
  const googleStrip = google ? google.panels.filter(googleAutoPanelVisible) : [];
  const fallbackCurrency = auto.panels.find((p) => p.money.length > 0)?.money[0]?.currency ?? 'AUD';
  const metaResultsCharts = auto.charts;
  const byTypeCharts = [byBucket('aia'), byBucket('meta_lead')].flatMap((p) =>
    p === undefined ? [] : p.charts.map((c) => ({ bucket: p.bucket, family: c })),
  );
  const hasCharts = metaResultsCharts.length > 0 || byTypeCharts.length > 0 || google !== null && google !== undefined;

  return (
    <>
      <HomeHero auto={auto} google={google} labels={labels} range={range} showsWebsite={showsWebsite} />

      <Band>
        <SectionHeading
          title="Meta"
          intro="Results from your Meta campaigns, by campaign type."
        />
        <div className="rpt-card-grid rpt-card-grid--2">
          {strip.map((p) => (
            <StripCard key={p.bucket} panel={p} />
          ))}
          {contactsVisible(auto.headline) && <ContactCard auto={auto} />}
        </div>
      </Band>

      {google && (
        <Band>
          <SectionHeading title="Google" intro="Vehicle listing ad, Search and Performance Max results." />
          {googleAutoNothingRan(google) ? (
            <p className="rpt-note rpt-muted" data-google-none>
              {GOOGLE_AUTO_NONE_SENTENCE}
            </p>
          ) : (
            <div className="rpt-card-grid rpt-card-grid--2">
              {googleStrip.map((p) => (
                <GoogleStripCard key={p.group} panel={p} />
              ))}
            </div>
          )}
        </Band>
      )}

      {showsWebsite && (
        <PlaceholderStrip title="Website" intro="Sessions, returning visitors, pages viewed and key events." />
      )}

      {hasCharts && (
        <Band variant="contrast">
          <SectionHeading
            title="Results over time"
            intro="Choose between the past 12 months and the reporting period."
          />
          <div className="rpt-card-grid rpt-card-grid--2">
            {metaResultsCharts.map((fam) => (
              <AutoFamilyChart
                key={`all-${fam.key}`}
                family={fam}
                section="All Meta campaigns"
                labels={labels}
                fallbackCurrency={fallbackCurrency}
                today={today}
              />
            ))}
            {google && (
              <GoogleAutoFamilyChart
                key="google-all"
                family={google.chart}
                section="All Google campaigns"
                labels={labels}
                fallbackCurrency={google.chart.currency ?? fallbackCurrency}
                today={today}
              />
            )}
            {byTypeCharts.map(({ bucket, family }) => (
              <AutoFamilyChart
                key={`${bucket}-${family.key}`}
                family={family}
                section={autoPanelSpecFor(bucket).title}
                labels={labels}
                fallbackCurrency={fallbackCurrency}
                today={today}
              />
            ))}
            {googleStrip.map((p) => (
              <GoogleAutoFamilyChart
                key={`google-${p.group}`}
                family={p.chart}
                section={googleAutoPanelSpecFor(p.group).title}
                labels={labels}
                fallbackCurrency={p.chart.currency ?? fallbackCurrency}
                today={today}
              />
            ))}
          </div>
        </Band>
      )}
    </>
  );
}
